import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../../config';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useOrganization } from '../../context/OrganizationContext';
import RunAgentModal from '../../components/RunAgentModal';

import {
  ArrowLeft,
  ArrowsClockwise,
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  Envelope,
  FileText,
  Lightning,
  Play,
  Pulse,
  Robot,
  SquaresFour,
  Timer,
  TrendUp,
  WebhooksLogo,
  XCircle
} from '@phosphor-icons/react';
// Event type icons
const EVENT_ICONS = {
  run: Play,
  webhook: WebhooksLogo,
  file: FileText,
  email: Envelope,
  api: Lightning,
  note: FileText,
  metric: TrendUp,
  output: FileText,
  artifact: FileText
};

// Event type colors
const EVENT_COLORS = {
  run: 'from-green-500 to-emerald-600',
  webhook: 'from-purple-500 to-violet-600',
  file: 'from-blue-500 to-cyan-600',
  email: 'from-orange-500 to-amber-600',
  api: 'from-pink-500 to-rose-600',
  note: 'from-gray-500 to-slate-600',
  metric: 'from-indigo-500 to-blue-600',
  output: 'from-teal-500 to-cyan-600',
  artifact: 'from-yellow-500 to-orange-600'
};

export default function AgentDashboard() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/agents/${agentId}/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-organization-id': activeOrganization?.organization_id || ''
        }
      });

      if (!response.ok) throw new Error('Failed to fetch dashboard');

      const data = await response.json();
      setDashboard(data.dashboard);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [agentId, activeOrganization]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <CircleNotch size={48} className="text-[#6366F1] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg mb-4">Error loading dashboard</p>
          <p className="text-[#A0AEC0] mb-6">{error}</p>
          <button
            onClick={() => navigate('/workspace/dashboards')}
            className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-medium"
          >
            Back to Dashboards
          </button>
        </div>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const agent = dashboard?.agent || {};
  const recentActivity = dashboard?.recentActivity || [];
  const recentOutputs = dashboard?.recentOutputs || [];

  return (
    <div className="min-h-screen bg-[#161B26] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/workspace/dashboards')}
              className="p-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                  <Robot size={20} className="text-white" />
                </div>
                {agent.name || 'Agent Dashboard'}
              </h1>
              <p className="text-[#A0AEC0] mt-1">
                {agent.description || 'Activity and metrics dashboard'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm ${
              agent.status === 'active' 
                ? 'bg-green-500/20 text-green-400'
                : 'bg-gray-500/20 text-gray-400'
            }`}>
              {agent.status || 'Unknown'}
            </span>
            <button
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
            >
              <ArrowsClockwise size={18} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* KPI Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#6366F1]/20 flex items-center justify-center">
                <Pulse size={20} className="text-[#6366F1]" />
              </div>
              <span className="text-[#A0AEC0] text-sm">Runs (7 days)</span>
            </div>
            <p className="text-3xl font-bold text-white">{kpis.runs7d || 0}</p>
            <p className="text-[#A0AEC0] text-xs mt-1">
              {kpis.runs30d || 0} total (30 days)
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CheckCircle size={20} className="text-green-400" />
              </div>
              <span className="text-[#A0AEC0] text-sm">Success Rate</span>
            </div>
            <p className="text-3xl font-bold text-white">{kpis.successRate || 0}%</p>
            <p className="text-[#A0AEC0] text-xs mt-1">
              {kpis.totalRuns || 0} total runs
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Timer size={20} className="text-purple-400" />
              </div>
              <span className="text-[#A0AEC0] text-sm">Avg Runtime</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {kpis.avgRuntimeSec ? `${kpis.avgRuntimeSec}s` : '--'}
            </p>
            <p className="text-[#A0AEC0] text-xs mt-1">per execution</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <Clock size={20} className="text-orange-400" />
              </div>
              <span className="text-[#A0AEC0] text-sm">Last Run</span>
            </div>
            <p className="text-xl font-bold text-white">
              {kpis.lastRunAt ? formatRelativeTime(kpis.lastRunAt) : 'Never'}
            </p>
            <p className="text-[#A0AEC0] text-xs mt-1">
              {kpis.lastRunAt ? formatDate(kpis.lastRunAt) : '--'}
            </p>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Pulse */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass rounded-2xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Pulse size={20} className="text-[#6366F1]" />
              Recent Pulse
            </h2>
            
            {recentActivity.length === 0 ? (
              <div className="text-center py-8">
                <Pulse size={48} className="text-[#6366F1]/30 mx-auto mb-3" />
                <p className="text-[#A0AEC0]">No activity recorded yet</p>
                <p className="text-[#A0AEC0] text-sm">Run your agent to see activity here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentActivity.map((event, index) => {
                  const Icon = EVENT_ICONS[event.type] || Pulse;
                  const colorClass = EVENT_COLORS[event.type] || 'from-gray-500 to-slate-600';
                  
                  return (
                    <div
                      key={event.id || index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {event.title}
                        </p>
                        {event.payload?.status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            event.payload.status === 'success' || event.payload.status === 'completed'
                              ? 'bg-green-500/20 text-green-400'
                              : event.payload.status === 'failed'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {event.payload.status}
                          </span>
                        )}
                        <p className="text-[#A0AEC0] text-xs mt-1">
                          {formatRelativeTime(event.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Recent Outputs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-2xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <FileText size={20} className="text-[#EC4899]" />
              Recent Outputs
            </h2>
            
            {recentOutputs.length === 0 ? (
              <div className="text-center py-8">
                <FileText size={48} className="text-[#EC4899]/30 mx-auto mb-3" />
                <p className="text-[#A0AEC0]">No outputs yet</p>
                <p className="text-[#A0AEC0] text-sm">Agent outputs will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentOutputs.map((output, index) => {
                  const Icon = EVENT_ICONS[output.type] || FileText;
                  const colorClass = EVENT_COLORS[output.type] || 'from-teal-500 to-cyan-600';
                  
                  return (
                    <div
                      key={output.id || index}
                      className="flex items-start gap-3 p-3 rounded-lg bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={16} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {output.title || output.payload?.filename || 'Output'}
                        </p>
                        {output.payload?.file_type && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 uppercase">
                            {output.payload.file_type}
                          </span>
                        )}
                        <p className="text-[#A0AEC0] text-xs mt-1">
                          {formatRelativeTime(output.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-6 flex justify-center gap-4"
        >
          <button
            onClick={() => navigate(`/agents/${agentId}`)}
            className="px-6 py-3 rounded-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            View Agent Details
          </button>
          <button
            onClick={() => setShowRunModal(true)}
            data-testid="run-agent-workspace-dashboard-btn"
            className="px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-medium transition-colors"
          >
            Run Agent
          </button>
        </motion.div>
      </div>

      {/* Run Agent Modal */}
      <RunAgentModal
        agent={{ id: agentId, name: dashboard?.agent?.name }}
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
        onSuccess={(sessionId) => { console.log('Session completed:', sessionId); }}
      />
    </div>
  );
}
